import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  FreshStrawFertPatch,
  usePatchFreshStrawFertilizationMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FreshStrawFertEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fertilizationId: string | null;
  mating?: string | null;
  initial?: Partial<FreshStrawFertPatch>;
  onSaved?: () => void;
}

type Draft = {
  ivcNumber: string;
  cleaved: string;
  transferClient: string;
  frozeClient: string;
  discardClient: string;
  heldTransfer: string;
  heldFrz: string;
  discardedGrade1: string;
  discardedGrade2: string;
  discardedGrade3: string;
  degenerates: string;
  loadedInStraws: string;
};

const EMPTY: Draft = {
  ivcNumber: '',
  cleaved: '',
  transferClient: '',
  frozeClient: '',
  discardClient: '',
  heldTransfer: '',
  heldFrz: '',
  discardedGrade1: '',
  discardedGrade2: '',
  discardedGrade3: '',
  degenerates: '',
  loadedInStraws: '',
};

function fromInitial(initial?: Partial<FreshStrawFertPatch>): Draft {
  if (!initial) return EMPTY;
  return {
    ivcNumber: initial.ivcNumber != null ? String(initial.ivcNumber) : '',
    cleaved: initial.cleaved != null ? String(initial.cleaved) : '',
    transferClient:
      initial.transferClient != null ? String(initial.transferClient) : '',
    frozeClient: initial.frozeClient != null ? String(initial.frozeClient) : '',
    discardClient:
      initial.discardClient != null ? String(initial.discardClient) : '',
    heldTransfer:
      initial.heldTransfer != null ? String(initial.heldTransfer) : '',
    heldFrz: initial.heldFrz != null ? String(initial.heldFrz) : '',
    discardedGrade1:
      initial.discardedGrade1 != null ? String(initial.discardedGrade1) : '',
    discardedGrade2:
      initial.discardedGrade2 != null ? String(initial.discardedGrade2) : '',
    discardedGrade3:
      initial.discardedGrade3 != null ? String(initial.discardedGrade3) : '',
    degenerates: initial.degenerates != null ? String(initial.degenerates) : '',
    loadedInStraws:
      initial.loadedInStraws != null ? String(initial.loadedInStraws) : '',
  };
}

function toInt(val: string): number | null {
  const n = parseInt(val, 10);
  return Number.isNaN(n) ? null : n;
}

export function FreshStrawFertEditDialog({
  open,
  onOpenChange,
  fertilizationId,
  mating,
  initial,
  onSaved,
}: FreshStrawFertEditDialogProps) {
  const dispatch = useDispatch();
  const [patchFert, { isLoading }] = usePatchFreshStrawFertilizationMutation();
  const [draft, setDraft] = useState<Draft>(() => fromInitial(initial));

  useEffect(() => {
    if (open) setDraft(fromInitial(initial));
  }, [open, initial]);

  const set =
    (field: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft((d) => ({ ...d, [field]: e.target.value }));

  const handleSave = async () => {
    if (!fertilizationId) return;
    const body: FreshStrawFertPatch = {
      ivcNumber: toInt(draft.ivcNumber),
      cleaved: toInt(draft.cleaved),
      transferClient: toInt(draft.transferClient),
      frozeClient: toInt(draft.frozeClient),
      discardClient: toInt(draft.discardClient),
      heldTransfer: toInt(draft.heldTransfer),
      heldFrz: toInt(draft.heldFrz),
      discardedGrade1: toInt(draft.discardedGrade1),
      discardedGrade2: toInt(draft.discardedGrade2),
      discardedGrade3: toInt(draft.discardedGrade3),
      degenerates: toInt(draft.degenerates),
      loadedInStraws: toInt(draft.loadedInStraws),
    };
    try {
      await patchFert({ fertilizationId, ...body }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Fertilization updated',
          life: ToastLife.SHORT,
        }),
      );
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Failed to update fertilization',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Edit Fertilization{mating ? ` — ${mating}` : ''}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <FieldGroup label="IVC Number">
            <NumberInput value={draft.ivcNumber} onChange={set('ivcNumber')} />
          </FieldGroup>
          <FieldGroup label="Cleaved">
            <NumberInput value={draft.cleaved} onChange={set('cleaved')} />
          </FieldGroup>
          <FieldGroup label="Loaded in Straws">
            <NumberInput
              value={draft.loadedInStraws}
              onChange={set('loadedInStraws')}
            />
          </FieldGroup>
          <FieldGroup label="Degenerates">
            <NumberInput
              value={draft.degenerates}
              onChange={set('degenerates')}
            />
          </FieldGroup>

          <FieldGroup label="Client Transfer">
            <NumberInput
              value={draft.transferClient}
              onChange={set('transferClient')}
            />
          </FieldGroup>
          <FieldGroup label="Client Frozen">
            <NumberInput
              value={draft.frozeClient}
              onChange={set('frozeClient')}
            />
          </FieldGroup>
          <FieldGroup label="Client Discarded">
            <NumberInput
              value={draft.discardClient}
              onChange={set('discardClient')}
            />
          </FieldGroup>

          <FieldGroup label="Held for Transfer">
            <NumberInput
              value={draft.heldTransfer}
              onChange={set('heldTransfer')}
            />
          </FieldGroup>
          <FieldGroup label="Held Frozen">
            <NumberInput value={draft.heldFrz} onChange={set('heldFrz')} />
          </FieldGroup>

          <FieldGroup label="Discarded Grade 1">
            <NumberInput
              value={draft.discardedGrade1}
              onChange={set('discardedGrade1')}
            />
          </FieldGroup>
          <FieldGroup label="Discarded Grade 2">
            <NumberInput
              value={draft.discardedGrade2}
              onChange={set('discardedGrade2')}
            />
          </FieldGroup>
          <FieldGroup label="Discarded Grade 3">
            <NumberInput
              value={draft.discardedGrade3}
              onChange={set('discardedGrade3')}
            />
          </FieldGroup>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={isLoading || !fertilizationId}
          >
            {isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-muted-foreground text-xs">{label}</label>
      {children}
    </div>
  );
}

function NumberInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <input
      type="number"
      value={value}
      onChange={onChange}
      className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2 py-1 text-sm focus-visible:ring-1 focus-visible:outline-none"
    />
  );
}
